import React from "react";
import { Avatar, Link } from "@mui/material";
import {
  Calendar,
  ChatTeardropDots,
  Envelope,
  Pencil,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  Printer,
  SealCheck,
  Signature,
} from "@phosphor-icons/react";
import { differenceInSeconds, format, formatDistanceToNow, isSameYear } from "date-fns";

import { CLIENT_STATUS, getBillingTypeLabel, DIAGNOSTIC_CODES, COUNTRIES } from "@constants";
import { formatFilename } from "utils/snakeCasetoTitleCase";
import AvatarInitials from "components/Header/HeaderSections/AvatarInitials";
import { getFormattedVOBLogs } from "./VOBUtils";
import DocumentSignatures from "./DocumentSignaturesUtils";
import {
  SYSTEM_LOG_TABLE_CONFIG,
  ENUM_DISPLAY_MAPS,
  type TableLogConfig,
} from "./SystemLogConfig";

export const filtersMap = {
  EMAIL: ["email"],
  EVENT: ["event", "video"],
  SYSTEM: ["system", "admin", "auth", "comment", "document", "authorization"],
  MESSAGE: ["message", "sms", "chat"],
  PHONE: ["phone", "call"],
  FAX: ["fax"],
  VOB: ["vob"],
  DOCUMENT_SIGNATURES: ["document_signatures"],
};

export const filtersMenuItems = [
  {
    label: "Emails",
    value: "EMAIL",
    icon: <Envelope weight="duotone" />,
  },
  {
    label: "Events",
    value: "EVENT",
    icon: <Calendar weight="duotone" />,
  },
  {
    label: "System",
    value: "SYSTEM",
    icon: <Pencil weight="duotone" />,
  },
  {
    label: "Messages",
    value: "MESSAGE",
    icon: <ChatTeardropDots weight="duotone" />,
  },
  {
    label: "Phone Calls",
    value: "PHONE",
    icon: <PhoneCall weight="duotone" />,
  },
  {
    label: "Faxes",
    value: "FAX",
    icon: <Printer weight="duotone" />,
  },
  {
    label: "VOBs",
    value: "VOB",
    icon: <SealCheck weight="duotone" />,
  },
  {
    label: "Document Signatures",
    value: "DOCUMENT_SIGNATURES",
    icon: <Signature weight="duotone" />,
  },
];

export const formatLogText = (...texts) => {
  return texts.map((text, index) => {
    if (index % 2 === 0) {
      return text + " ";
    } else {
      return (
        <span key={index} style={{ fontWeight: 500, color: "#31333a" }}>
          {text}{" "}
        </span>
      );
    }
  });
};

export function getArticle(word) {
  if (!word || typeof word !== "string") return;
  const vowels = ["a", "e", "i", "o", "u"];
  return vowels.includes(word[0].toLowerCase()) ? "an" : "a";
}

function getAuthorizationText(text) {
  text = String(text);

  const wordMap = {
    pre_auth: "Pre-Authorization",
    re_auth: "Re-Authorization",
    auth: "Ongoing Authorization",
    authorization_type: "Authorization Type",
    authorization_code: "Authorization Number",
    end_date: "Authorization End Date",
    start_date: "Authorization Start Date",
  };
  for (const [key, value] of Object.entries(wordMap)) {
    text = text.replace(new RegExp(`\\b${key}\\b`, "g"), value);
  }
  return text;
}

const getFormattedAuthLog = (obj, clinicianList = []) => {
  const { data_changes, event_action, after, before } = obj;

  if (!data_changes) {
    if (event_action === "INSERT" && after.code) {
      return formatLogText(`added`, `${after.code}`, `to an ongoing authorization`);
    }
  } else {
    const dataChangeKey = Object.keys(data_changes)?.[0];

    if (dataChangeKey === "approved_units") {
      if (after && before) {
        return formatLogText(
          `changed the authorization`,
          `${after.code}`,
          `units to`,
          `${after.approved_units} `,
          "from",
          `${before.approved_units} `
        );
      }
    }

    if (dataChangeKey === "code") {
      return formatLogText(`added`, `${data_changes.code?.new_value}`, `to an authorization`);
    }

    if (dataChangeKey === "pending_auth") {
      return formatLogText(
        `turned`,
        `${data_changes.pending_auth?.new_value ? "On" : "Off"}`,
        `temporary and pending authorization`
      );
    }

    if (dataChangeKey === "clinician_id") {
      const oldClinicianName = clinicianList.find(
        (clinician) => clinician.clinician_id == data_changes.clinician_id?.old
      );
      const newClinicianName = clinicianList.find(
        (clinician) => clinician.clinician_id == data_changes.clinician_id?.new_value
      );
      return formatLogText(
        `Changed the authorization approved clinician to`,
        `${newClinicianName.first_name || ""} ${newClinicianName.last_name || ""}`,
        "from",
        `${oldClinicianName.first_name || ""} ${oldClinicianName.last_name || ""} `
      );
    }

    const dataKeysOnChanges = [
      "authorization_type",
      "approved_time",
      "authorization_number",
      "end_date",
      "start_date",
      "authorization_code",
    ];

    if (dataKeysOnChanges.includes(dataChangeKey))
      return formatLogText(
        `Changed the`,
        `${formatFilename(getAuthorizationText(dataChangeKey))}`,
        `to`,
        `${data_changes[dataChangeKey]?.new_value !== "" ? getAuthorizationText(data_changes[dataChangeKey].new_value) : "none"}`,
        "from",
        `${data_changes[dataChangeKey]?.old !== "" ? getAuthorizationText(data_changes[dataChangeKey].old) : "none"}`
      );
  }
};

const getProfileUpdateText = (data_changes) => {
  const dataChangeKey = Object.keys(data_changes)?.[0];

  const logMessages = {
    zip_code: "zip code",
    name: "child's name",
    gender: "gender",
    pronouns: "pronouns",
    date_of_birth: "date of birth",
    child_last_name: "child's last name",
    identified_gender: "identified gender",
    preferred_first_name: "preferred first name",
    transgender: "transgender",
    city: "city",
    state: "state",
    street_1: "address",
    street_2: "address 2",
    ethnicity: "ethnicity",
    lives_with: "lives with",
    primary_language: "primary language",
    needs_interpreter: "needs interpreter",
    pre_auth_required: "pre-authorization required",
    requires_tablet: "requires tablet",
    phone: "phone number",
    legal_guardian: "legal guardian",
    relationship_to_client: "relationship to client",
    email: "email",
    timezone: "time zone",
    first_name: "first name",
    last_name: "last name",
    // Privacy consent fields
    agree_to_research_participation: "research participation consent",
    send_email: "electronic communication consent",
    // Client Diagnosis
    diagnosis_id: "Diagnosis",
    diagnosis_date: "Diagnosis Date",
    additional_diagnoses: "Additional Diagnosis",
    medical_diagnoses: "Medical Diagnosis",
    cars_2_type: "CARS-2 type",
    cars_2_t_score: "CARS-2 T-score",
    iq_test: "IQ test on file",
    iq_score: "Full Scale IQ Score",
    treatment_modality: "Treatment Modality",
    asd_severity_social: "ASD Severity - Social Communication",
    asd_severity_restricted: "ASD Severity - Restricted, Repetitive Behaviors",
    // Personal Information — emergency contacts
    country: "country",
    emergency_contact_name: "emergency contact",
    emergency_contact_phone_number: "emergency contact phone number",
    emergency_contact_relationship_to_client: "emergency contact relationship to client",
    // Client Diagnosis — expanded fields
    evaluation_comorbid_diagnoses: "evaluation for comorbid diagnoses",
    other_symptoms_impairments: "other symptoms and functional impairments observed",
    persistent_deficits_social: "persistent deficits in social communication",
    restrictive_repetitive_patterns: "restrictive, repetitive patterns of behavior",
    // Education — field on client table
    special_education_iep: "special education services & IEP",
    // Intervention History — fields on client table
    has_received_services: "has received other intervention services",
    barriers_to_access: "barriers preventing access to services",
    // Medications — fields on client table
    medication_adherence: "medication adherence",
    coordination_care_notes: "coordination of care notes",
  };

  if (logMessages[dataChangeKey]) {
    let { new_value, old } = data_changes[dataChangeKey];

    // The diagnosis_id is 1-indexed, so we need to subtract 1 to get the correct diagnostic code
    if (dataChangeKey === "diagnosis_id") {
      new_value = DIAGNOSTIC_CODES[new_value - 1];
      old = DIAGNOSTIC_CODES[old - 1];
    }

    // The additional_diagnoses is an array of diagnostic codes, so we need to map the labels
    if (dataChangeKey === "additional_diagnoses") {
      new_value = [...new_value].map((diagnosis) => diagnosis?.label).join(", ");
      old = Array.isArray(old) ? old.map((diagnosis) => diagnosis?.label).join(", ") : old;
    }

    if (dataChangeKey === "medical_diagnoses") {
      new_value = [...new_value].join(", ");
      old = Array.isArray(old) ? old.join(", ") : old;
    }

    if (dataChangeKey === "country") {
      new_value = COUNTRIES[new_value] || new_value;
      old = COUNTRIES[old] || old;
    }

    if (dataChangeKey === "barriers_to_access") {
      new_value = Array.isArray(new_value) ? new_value.join(", ") : new_value;
      old = Array.isArray(old) ? old.join(", ") : old;
    }

    if (ENUM_DISPLAY_MAPS[dataChangeKey]) {
      new_value = ENUM_DISPLAY_MAPS[dataChangeKey][new_value] || new_value;
      old = ENUM_DISPLAY_MAPS[dataChangeKey][old] || old;
    }

    if (old === true) old = "yes";
    if (old === false) old = "no";
    if (new_value === true) new_value = "yes";
    if (new_value === false) new_value = "no";

    let actionWord = "changed";

    if (!old) actionWord = "updated";
    else if (!new_value) actionWord = "removed";

    const newValue = new_value;
    const oldValue = old;
    // Validation to avoid logs showing changes from null values to "" for example
    if (!old && !new_value) return null;

    let formattedLogText = formatLogText(
      `${actionWord} ${logMessages[dataChangeKey]} to`,
      newValue,
      "from",
      oldValue
    );
    if (actionWord === "removed")
      formattedLogText = formatLogText(`${actionWord} ${logMessages[dataChangeKey]}`);
    if (actionWord === "updated")
      formattedLogText = formatLogText(`${actionWord} ${logMessages[dataChangeKey]} to`, newValue);
    return formattedLogText;
  } else return null; // Show logs of known fields only
};

/**
 * Transforms a raw enum value to its display label using the ENUM_DISPLAY_MAPS config.
 * Falls back to replacing underscores with spaces and lowercasing if no map is found.
 */
function transformEnumValue(fieldKey: string, value: unknown): string {
  if (value == null || value === "") return "";
  const strValue = String(value);
  const enumMap = ENUM_DISPLAY_MAPS[fieldKey];
  if (enumMap) return enumMap[strValue] || strValue;
  return strValue.replace(/_/g, " ").toLowerCase();
}

/**
 * Transforms a field value for display in log text. Handles arrays, booleans, and enums.
 */
function transformFieldValue(fieldKey: string, value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (value === true) return "yes";
  if (value === false) return "no";
  if (ENUM_DISPLAY_MAPS[fieldKey]) return transformEnumValue(fieldKey, value);
  return String(value);
}

/**
 * Formats UPDATE log text for standard (single-row, upserted) tables.
 * Uses same action-word pattern as getProfileUpdateText but looks up labels from config.
 */
const getStandardTableUpdateText = (
  data_changes: Record<string, { new_value: unknown; old: unknown }>,
  config: TableLogConfig
) => {
  if (!data_changes) return null;
  const dataChangeKey = Object.keys(data_changes)?.[0];
  const label = config.fields[dataChangeKey];
  if (!label) return null;

  const newValue = transformFieldValue(dataChangeKey, data_changes[dataChangeKey]?.new_value);
  const oldValue = transformFieldValue(dataChangeKey, data_changes[dataChangeKey]?.old);

  if (!oldValue && !newValue) return null;

  let actionWord = "changed";
  if (!oldValue) actionWord = "updated";
  else if (!newValue) actionWord = "removed";

  if (actionWord === "removed") return formatLogText(`${actionWord} ${label}`);
  if (actionWord === "updated") return formatLogText(`${actionWord} ${label} to`, newValue);
  return formatLogText(`${actionWord} ${label} to`, newValue, "from", oldValue);
};

/**
 * Returns the row identifier value from the log's after or before JSONB for display in
 * table-entry and external-provider log sentences.
 */
function getRowIdentifier(logObject: Record<string, unknown>, config: TableLogConfig): string {
  if (!config.rowIdentifierField) return "";
  const after = logObject.after as Record<string, unknown> | undefined;
  const before = logObject.before as Record<string, unknown> | undefined;
  const value = after?.[config.rowIdentifierField] ?? before?.[config.rowIdentifierField];
  return value != null ? String(value) : "";
}

/**
 * Formats UPDATE log text for table-entry and external-provider tables.
 * Includes row identifier and table label in the sentence.
 */
const getTableEntryUpdateText = (logObject: Record<string, unknown>, config: TableLogConfig) => {
  const data_changes = logObject.data_changes as Record<
    string,
    { new_value: unknown; old: unknown }
  >;
  if (!data_changes) return null;

  const dataChangeKey = Object.keys(data_changes)?.[0];
  const label = config.fields[dataChangeKey];
  if (!label) return null;

  const newValue = transformFieldValue(dataChangeKey, data_changes[dataChangeKey]?.new_value);
  const oldValue = transformFieldValue(dataChangeKey, data_changes[dataChangeKey]?.old);

  if (!oldValue && !newValue) return null;

  const rowId = getRowIdentifier(logObject, config);
  const context =
    config.type === "external_provider"
      ? rowId
        ? ` for ${rowId}`
        : ""
      : rowId
        ? ` for ${rowId} in ${config.tableLabel}`
        : ` in ${config.tableLabel}`;

  let actionWord = "changed";
  if (!oldValue) actionWord = "updated";
  else if (!newValue) actionWord = "removed";

  if (actionWord === "removed") return formatLogText(`${actionWord} ${label}${context}`);
  if (actionWord === "updated")
    return formatLogText(`${actionWord} ${label}${context} to`, newValue);
  return formatLogText(`${actionWord} ${label}${context} to`, newValue, "from", oldValue);
};

/**
 * Formats INSERT log text for table-entry and external-provider tables.
 */
const getTableEntryInsertText = (logObject: Record<string, unknown>, config: TableLogConfig) => {
  const rowId = getRowIdentifier(logObject, config);
  if (rowId) {
    return formatLogText(`added`, rowId, `to ${config.tableLabel}`);
  }
  return formatLogText(`added a new`, `${config.tableLabel}`, "entry");
};

/**
 * Formats DELETE log text for table-entry and external-provider tables.
 */
const getTableEntryDeleteText = (logObject: Record<string, unknown>, config: TableLogConfig) => {
  const rowId = getRowIdentifier(logObject, config);
  if (rowId) {
    return formatLogText(`removed`, rowId, `from ${config.tableLabel}`);
  }
  return formatLogText(`removed a`, `${config.tableLabel}`, "entry");
};

export const getLogText = (
  clientName = "AnswersNow Client",
  documentTypes,
  logObject,
  clinicianList = []
) => {
  const {
    type: logType,
    is_outbound,
    event_action,
    billing_type,
    new_status,
    old_status,
    document_type,
    data_changes,
    table_name,
  } = logObject;

  const type = logType.toLowerCase();

  if (type === "email")
    return formatLogText("sent an email to", is_outbound ? clientName : "AnswersNow");
  if (filtersMap.EVENT.includes(type)) {
    if (type === "video") return "hosted a video event";
    const billingTypeLabel = getBillingTypeLabel(billing_type);
    return formatLogText(
      `${event_action.toLowerCase()} ${getArticle(billingTypeLabel) || "an"} `,
      billingTypeLabel,
      " event"
    );
  }

  if (type === "authorization") {
    if (logObject.table_name === "secondary_insurance_authorizations" && logObject.data_changes) {
      return getFormattedAuthLog(logObject, clinicianList);
    } else if (logObject.table_name === "secondary_insurance_authorizations")
      return formatLogText("added a new ", "Secondary ", "authorization");

    if (
      logObject.table_name === "insurance_authorizations" &&
      logObject.data_changes &&
      Object.keys(data_changes)?.[0] !== "inactive_date"
    ) {
      return getFormattedAuthLog(logObject, clinicianList);
    }

    if (logObject.after?.authorization_type && event_action === "INSERT") {
      return formatLogText(
        "added a new",
        getAuthorizationText(logObject.after?.authorization_type)
      );
    }

    //checks if changes come from table approved_time
    if (logObject.table_name === "approved_time") {
      return getFormattedAuthLog(logObject);
    }
  }

  if (type === "vob") {
    return getFormattedVOBLogs(logObject);
  }

  if (type === "document_signatures") {
    if (
      event_action === "UPDATE" &&
      logObject.after.document_id &&
      logObject.table_name !== "treatment_plans"
    ) {
      return <DocumentSignatures documentId={logObject.after.document_id} />;
    } else if (event_action === "UPDATE" && logObject.table_name === "treatment_plans") {
      return formatLogText(` signed a `, `Treatment Plan`);
    }
  }

  if (type === "system") {
    try {
      const tableConfig = SYSTEM_LOG_TABLE_CONFIG[table_name];

      if (event_action === "UPDATE") {
        if (tableConfig?.type === "table_entry" || tableConfig?.type === "external_provider") {
          return getTableEntryUpdateText(logObject, tableConfig);
        }
        if (tableConfig?.type === "standard") {
          return getStandardTableUpdateText(data_changes, tableConfig);
        }
        return getProfileUpdateText(data_changes);
      }

      if (event_action === "INSERT") {
        if (tableConfig?.type === "table_entry" || tableConfig?.type === "external_provider") {
          return getTableEntryInsertText(logObject, tableConfig);
        }
        if (table_name === "client_diagnosis") {
          return formatLogText("added a new", "Diagnosis");
        }
      }

      if (event_action === "DELETE") {
        if (tableConfig?.type === "table_entry" || tableConfig?.type === "external_provider") {
          return getTableEntryDeleteText(logObject, tableConfig);
        }
        if (table_name === "client_diagnosis") {
          return formatLogText("removed a", "Diagnosis");
        }
      }

      if (new_status && old_status) {
        return formatLogText(
          "changed status to",
          CLIENT_STATUS[new_status] || new_status,
          "from",
          CLIENT_STATUS[old_status] || old_status
        );
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }
  if (type === "document") {
    const docDisplayName = documentTypes.find((doc) => doc.name === document_type)?.display_name;
    if (document_type === "ONBOARDING_PACKET")
      return formatLogText("completed the Onboarding Packet");
    return formatLogText(
      `${event_action.toLowerCase()} ${getArticle(document_type)}`,
      docDisplayName || document_type,
      "document"
    );
  }
  if (filtersMap.MESSAGE.includes(type)) {
    if (type === "sms")
      return formatLogText(
        "started a SMS conversation with",
        is_outbound ? clientName : "AnswersNow"
      );
    if (type === "chat")
      return formatLogText("sent a chat message to ", is_outbound ? clientName : "AnswersNow");
    return "sent a message";
  }
  if (filtersMap.PHONE.includes(type))
    return formatLogText("placed a call to", is_outbound ? clientName : "AnswersNow");
  if (filtersMap.FAX.includes(type)) return "faxed a document";
};

export const getLogIcon = (theme, { type: logType, is_outbound, profile_img, user_initials }) => {
  const iconProps = {
    size: "16px",
    weight: "duotone",
    color: theme.palette.action.active,
  };
  const type = logType.toLowerCase();

  if (filtersMap.EMAIL.includes(type)) return <Envelope {...iconProps} />;
  if (filtersMap.EVENT.includes(type)) return <Calendar {...iconProps} />;
  if (filtersMap.SYSTEM.includes(type)) {
    if (type === "comment") {
      if (profile_img) return <Avatar src={profile_img} sx={{ width: 16, height: 16 }} />;
      else return <AvatarInitials userInitials={user_initials} size={16} />;
    }
    return <Pencil {...iconProps} />;
  }
  if (filtersMap.MESSAGE.includes(type)) return <ChatTeardropDots {...iconProps} />;
  if (filtersMap.PHONE.includes(type))
    return is_outbound ? <PhoneOutgoing {...iconProps} /> : <PhoneIncoming {...iconProps} />;
  if (filtersMap.FAX.includes(type)) return <Printer {...iconProps} />;
  if (filtersMap.VOB.includes(type)) return <SealCheck {...iconProps} />;
  if (filtersMap.DOCUMENT_SIGNATURES.includes(type)) return <Signature {...iconProps} />;
  return <Pencil {...iconProps} />;
};

export const getOpenConversation = (
  history,
  { type: logType, conversation_count, sent_by_user_id, team_member_user_id, sms_msg_id }
) => {
  const type = logType.toLowerCase();
  const url = `/messages?chat_id=${team_member_user_id}&msg_id=${sms_msg_id}`;
  const element = (
    <>
      {" "}
      (
      <Link underline="hover" onClick={() => history.push(url)}>
        open conversation
      </Link>
      )
    </>
  );
  if (type === "sms" && conversation_count && sent_by_user_id) {
    return element;
  }
};

function formatDistanceToNowCustom(date) {
  const distance = formatDistanceToNow(date, { addSuffix: true });
  return distance === "less than a minute ago" ? "Just now" : distance;
}

export function formattedLogDate(logDate) {
  const logDateFormat = isSameYear(logDate, new Date()) ? "MMMM do" : "PPP";
  const formattedDate = format(logDate, `${logDateFormat} p`);

  // Up to third option in
  // formatDistanceToNow table https://date-fns.org/v3.6.0/docs/formatDistanceToNow
  const maxDurationInSeconds = 44 * 60 + 30; // 44 mins 30 secs in seconds
  const timeDifferenceInSeconds = differenceInSeconds(new Date(), logDate);

  if (timeDifferenceInSeconds <= maxDurationInSeconds) {
    const timeAgo = formatDistanceToNowCustom(logDate);
    return timeAgo;
  }

  return formattedDate;
}
